// Horizontal scrolling row of clips with staged reveal and preload behavior.

import React from 'react';
import type { Clip } from '@airhead-games/shared/types/configs/media/clipLibrary';
import { useClipBatching } from '../../hooks';
import { Button } from '../ui/button';
import { ScrollArea, ScrollBar } from '../ui/scroll-area';
import Card from './Card';

interface RailProps {
  clips: Clip[];
  showGameChip?: boolean;
  showChips?: boolean;
  width?: number;
  height?: number;
  initialEagerCount?: number;
  batchSize?: number;
  onLoadMore?: (nextVisibleCount: number, totalCount: number) => void;
}

const Rail: React.FC<RailProps> = ({
  clips,
  showGameChip = true,
  showChips = true,
  width = 534,
  height = 300,
  initialEagerCount = 3,
  batchSize,
  onLoadMore,
}) => {
  const {
    eagerCount,
    hasMore,
    mountedClips,
    isClipVisible,
    handleClipLoad,
    handleLoadMore,
  } = useClipBatching({
    clips,
    batchSize,
    initialEagerCount,
    onLoadMore,
  });

  if (clips.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <ScrollArea className="w-full">
        <div className="relative flex gap-6 py-4">
          {mountedClips.map((clip, index) => {
            const isVisible = isClipVisible(index);

            return (
              <div
                key={clip.clipUrl}
                aria-hidden={isVisible ? undefined : true}
                className={
                  isVisible
                    ? 'shrink-0'
                    : 'pointer-events-none absolute top-0 -left-[9999px] h-0 w-0 overflow-hidden opacity-0'
                }
                style={isVisible ? { width } : undefined}
              >
                <Card
                  clipUrl={clip.clipUrl}
                  streamerName={clip.streamerName}
                  gameId={clip.gameId}
                  width={width}
                  height={height}
                  showGameChip={showGameChip}
                  showChips={isVisible ? showChips : false}
                  eager={index < eagerCount}
                  interactive={isVisible}
                  onLoad={() => handleClipLoad(clip.clipUrl)}
                />
              </div>
            );
          })}
          {hasMore && (
            <div
              className="flex shrink-0 items-center justify-center"
              style={{ minHeight: height, width: Math.min(width * 0.45, 240) }}
            >
              <Button variant="outline" size="lg" onClick={handleLoadMore}>
                Load more clips
              </Button>
            </div>
          )}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
};

export default Rail;
